import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import BusTripApi from "Services/BusTrip";
import BusRouteApi from "Services/BusRoute";

const toDateTimeLocal = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const BusTripCreateModal = ({ openDialog, handleClose, tripId = "" }) => {
  const dispatch = useDispatch();
  const [routes, setRoutes] = useState([]);
  const isEditMode = Boolean(tripId);

  const normalizeRoutes = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.routes)) return payload.routes || [];
    if (Array.isArray(payload?.data?.routes)) return payload.data.routes || [];
    if (Array.isArray(payload?.data?.data)) return payload.data.data || [];
    if (Array.isArray(payload?.data)) return payload.data || [];
    return [];
  };

  useEffect(() => {
    if (!openDialog) return;
    if (!isEditMode) formik.resetForm();

    BusRouteApi.getBusRoutesList({ page: 1, limit: 1000, search: "" }).then(
      (res) => setRoutes(normalizeRoutes(res)),
      () => {
        setRoutes([]);
        dispatch(
          showSnackbar({
            message: "Unable to load bus routes",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [openDialog, isEditMode]);

  useEffect(() => {
    if (!openDialog || !isEditMode || !tripId) return;
    dispatch(showLoader("Loading please wait..."));
    BusTripApi.getBusTripById(tripId).then(
      (res) => {
        dispatch(hideLoader());
        const data = res?.data || {};
        formik.setValues({
          route_id: data?.route_id || "",
          departure_at: toDateTimeLocal(data?.departure_at),
          arrival_at: toDateTimeLocal(data?.arrival_at),
          total_seats: Number(data?.total_seats ?? 45),
          available_seats: Number(data?.available_seats ?? 45),
          base_price: Number(data?.base_price ?? 5000),
          currency: data?.currency || "XAF",
          status: data?.status || "scheduled",
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Unable to load bus trip details",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [openDialog, isEditMode, tripId, dispatch]);

  const formik = useFormik({
    initialValues: {
      route_id: "",
      departure_at: "",
      arrival_at: "",
      total_seats: 45,
      available_seats: 45,
      base_price: 5000,
      currency: "XAF",
      status: "scheduled",
    },
    validationSchema: yup.object({
      route_id: yup.string().required("Route is required"),
      departure_at: yup.string().required("Departure is required"),
      arrival_at: yup.string().required("Arrival is required"),
      total_seats: yup.number().required("Total seats is required").min(1, "Minimum is 1"),
      available_seats: yup.number().required("Available seats is required").min(0, "Minimum is 0"),
      base_price: yup.number().required("Base price is required").min(0, "Minimum is 0"),
      currency: yup.string().required("Currency is required"),
      status: yup.string().required("Status is required"),
    }),
    onSubmit: (values) => {
      const payload = {
        route_id: values.route_id,
        departure_at: new Date(values.departure_at).toISOString(),
        arrival_at: new Date(values.arrival_at).toISOString(),
        total_seats: Number(values.total_seats),
        available_seats: Number(values.available_seats),
        base_price: Number(values.base_price),
        currency: values.currency,
        status: values.status,
      };

      dispatch(showLoader("Loading please wait..."));
      const request = isEditMode ? BusTripApi.updateBusTrip(tripId, payload) : BusTripApi.createBusTrip(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Bus trip updated successfully" : "Bus trip created successfully"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            handleClose(true);
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Error while updating bus trip" : "Error while creating bus trip"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  const getRouteId = (route) => route?.id || route?._id || "";
  const getRouteLabel = (route) => {
    const code = route?.route_code || getRouteId(route) || "-";
    const origin = route?.places_bus_routes_origin_place_idToplaces?.name || route?.origin_place_id || "-";
    const destination = route?.places_bus_routes_destination_place_idToplaces?.name || route?.destination_place_id || "-";
    return `${code} (${origin} - ${destination})`;
  };

  return (
    <Dialog open={openDialog} onClose={() => handleClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? "Edit Bus Trip" : "Add Bus Trip"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Route"
              name="route_id"
              value={formik.values.route_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.route_id && Boolean(formik.errors.route_id)}
              helperText={formik.touched.route_id && formik.errors.route_id}
            >
              <MenuItem value="">Select Route</MenuItem>
              {routes.map((route) => {
                const routeId = getRouteId(route);
                if (!routeId) return null;
                return (
                  <MenuItem key={routeId} value={routeId}>
                    {getRouteLabel(route)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Departure At"
              name="departure_at"
              InputLabelProps={{ shrink: true }}
              value={formik.values.departure_at}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.departure_at && Boolean(formik.errors.departure_at)}
              helperText={formik.touched.departure_at && formik.errors.departure_at}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Arrival At"
              name="arrival_at"
              InputLabelProps={{ shrink: true }}
              value={formik.values.arrival_at}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.arrival_at && Boolean(formik.errors.arrival_at)}
              helperText={formik.touched.arrival_at && formik.errors.arrival_at}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Total Seats" name="total_seats" value={formik.values.total_seats} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.total_seats && Boolean(formik.errors.total_seats)} helperText={formik.touched.total_seats && formik.errors.total_seats} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Available Seats" name="available_seats" value={formik.values.available_seats} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.available_seats && Boolean(formik.errors.available_seats)} helperText={formik.touched.available_seats && formik.errors.available_seats} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="number" label="Base Price" name="base_price" value={formik.values.base_price} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.base_price && Boolean(formik.errors.base_price)} helperText={formik.touched.base_price && formik.errors.base_price} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Currency" name="currency" value={formik.values.currency} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.currency && Boolean(formik.errors.currency)} helperText={formik.touched.currency && formik.errors.currency} />
          </Grid>
          <Grid item xs={12}>
            <TextField select fullWidth label="Status" name="status" value={formik.values.status} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.status && Boolean(formik.errors.status)} helperText={formik.touched.status && formik.errors.status}>
              <MenuItem value="scheduled">scheduled</MenuItem>
              <MenuItem value="boarding">boarding</MenuItem>
              <MenuItem value="departed">departed</MenuItem>
              <MenuItem value="arrived">arrived</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>{isEditMode ? "Update" : "Add"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusTripCreateModal;
